import {
  billingFeatureFallbackMessage,
  myPricesRefreshCooldownMessage,
  paywallCopy,
  seatLimitFallbackMessage,
} from '../billing/paywallCopy'
import { ApiError } from './client'

export type BillingErrorBody = {
  error?: string
  message?: string
  current_plan?: string
  required_plan?: string
  required_plan_display_name?: string
  next_available_at?: string
  active_device_label?: string
  active_device_last_seen?: string
}

export type BillingErrorCode =
  | 'device_limit'
  | 'seat_limit'
  | 'feature_not_available'
  | 'my_prices_supplier_limit'
  | 'my_prices_refresh_cooldown'
  | 'venue_limit'
  | 'venue_addon_required'

export type BillingErrorDetails = {
  errorCode: BillingErrorCode
  message: string
  currentPlan?: string
  requiredPlan?: string
  requiredPlanDisplayName?: string
  nextAvailableAt?: string
  activeDeviceLabel?: string
  activeDeviceLastSeen?: string
}

const billingErrorCodes = new Set<string>([
  'device_limit',
  'seat_limit',
  'feature_not_available',
  'my_prices_supplier_limit',
  'my_prices_refresh_cooldown',
  'venue_limit',
  'venue_addon_required',
])

export const parseApiErrorBody = (error: ApiError): BillingErrorBody | null => {
  try {
    return JSON.parse(error.message) as BillingErrorBody
  } catch {
    return null
  }
}

export const parseBillingError = (error: unknown): BillingErrorDetails | null => {
  if (!(error instanceof ApiError) || error.status !== 403) {
    return null
  }
  const body = parseApiErrorBody(error)
  if (!body?.error || !billingErrorCodes.has(body.error)) {
    return null
  }
  return {
    errorCode: body.error as BillingErrorCode,
    message:
      body.message ??
      defaultBillingErrorMessage(
        body.error,
        body.required_plan_display_name,
        body.next_available_at,
        body.current_plan,
      ),
    currentPlan: body.current_plan,
    requiredPlan: body.required_plan,
    requiredPlanDisplayName: body.required_plan_display_name,
    nextAvailableAt: body.next_available_at,
    activeDeviceLabel: body.active_device_label,
    activeDeviceLastSeen: body.active_device_last_seen,
  }
}

export type DeviceLimitDetails = {
  message: string
  activeDeviceLabel?: string
  activeDeviceLastSeen?: string
}

export const deviceLimitDetails = (error: unknown): DeviceLimitDetails | null => {
  const billing = parseBillingError(error)
  if (billing?.errorCode !== 'device_limit') {
    return null
  }
  return {
    message: billing.message,
    activeDeviceLabel: billing.activeDeviceLabel,
    activeDeviceLastSeen: billing.activeDeviceLastSeen,
  }
}

export const billingErrorMessage = (error: unknown): string =>
  parseBillingError(error)?.message ?? 'Действие недоступно на вашем тарифе.'

const defaultBillingErrorMessage = (
  errorCode: string,
  requiredPlanDisplayName?: string,
  nextAvailableAt?: string,
  currentPlan?: string,
): string => {
  if (errorCode === 'device_limit') {
    return paywallCopy.deviceLimit
  }
  if (errorCode === 'seat_limit') {
    return seatLimitFallbackMessage(currentPlan)
  }
  if (errorCode === 'my_prices_supplier_limit') {
    return paywallCopy.myPricesSupplierLimit
  }
  if (errorCode === 'my_prices_refresh_cooldown') {
    return myPricesRefreshCooldownMessage(nextAvailableAt)
  }
  if (errorCode === 'venue_limit') {
    return paywallCopy.venueLimitStartStandard
  }
  if (errorCode === 'venue_addon_required') {
    return paywallCopy.venueAddonRequired
  }
  return billingFeatureFallbackMessage(requiredPlanDisplayName)
}

export const billingSupportMailto = (subject: string) =>
  `mailto:support@provisium.ru?subject=${encodeURIComponent(subject)}`
